import { createSelector } from '@reduxjs/toolkit';

import { normalizeFieldKey } from 'editor/src/store/design/util/fieldKeys';
import type { RootState } from 'editor/src/store/index';

import type { FieldValueSource } from '../types';

const NONE: FieldValueSource[] = [];

/**
 * The host's field list, reduced to entries the author can pick: a label and a key
 * in the form the fill matches (see normalizeFieldKey). The list arrives over
 * postMessage, so anything else is dropped rather than trusted; the first entry
 * for a key wins.
 */
const getFieldValueSources = createSelector(
  (state: RootState) => state.hostSettings.fieldValueSources,
  (sources): FieldValueSource[] => {
    if (!Array.isArray(sources)) {
      return NONE;
    }
    const seen = new Set<string>();
    const valid = sources.flatMap((source: unknown) => {
      if (!source || typeof source !== 'object') {
        return [];
      }
      const { key, label } = source as Record<string, unknown>;
      const normalized = typeof key === 'string' ? normalizeFieldKey(key) : undefined;
      if (!normalized || seen.has(normalized) || typeof label !== 'string' || !label.trim()) {
        return [];
      }
      seen.add(normalized);
      return [{ key: normalized, label: label.trim() }];
    });
    return valid.length ? valid : NONE;
  },
);

export default getFieldValueSources;
